import {
  chartServer,
  formatBytes,
  packTotalBytes,
  type PackDescriptor,
  type PackManifest,
} from "../storage/manifest";
import {
  installPack,
  installStates,
  removePack,
  type InstallState,
} from "../storage/installer";
import { requestPersistence, storageBudget, type StorageBudget } from "../storage/opfs";

/**
 * Chrome and Safari both refuse writes well before the reported quota, and a
 * chart pack that stops halfway is worse than one never started, so the
 * selection is held back from the very top of the budget.
 */
const HEADROOM_BYTES = 64 * 1024 * 1024;

interface SetupOptions {
  manifest: PackManifest;
  onDone: () => void;
  /** True when the map is already usable and this is a management visit. */
  canCancel: boolean;
}

export class SetupScreen {
  private readonly root = document.createElement("div");
  private selection = new Set<string>();
  private states = new Map<string, InstallState>();
  private budget: StorageBudget = { quota: 0, usage: 0, available: 0, persisted: false };
  private busy = false;
  private error: string | null = null;
  private abort: AbortController | null = null;
  private progress = new Map<string, number>();

  constructor(private readonly options: SetupOptions) {
    this.root.className = "setup";
  }

  get element(): HTMLElement {
    return this.root;
  }

  async start(): Promise<void> {
    await requestPersistence();
    await this.refresh();
  }

  private get packs(): PackDescriptor[] {
    return this.options.manifest.packs;
  }

  private async refresh(): Promise<void> {
    this.states = await installStates(this.packs);
    this.budget = await storageBudget();
    // Preselect required packs and anything already (partly) on disk.
    for (const pack of this.packs) {
      const state = this.states.get(pack.id);
      if (pack.required || state === "installed" || state === "partial") {
        this.selection.add(pack.id);
      }
    }
    this.render();
  }

  private selectedPacks(): PackDescriptor[] {
    return this.packs.filter((pack) => this.selection.has(pack.id));
  }

  /** Bytes still to fetch for the current selection. */
  private outstandingBytes(): number {
    return this.selectedPacks()
      .filter((pack) => this.states.get(pack.id) !== "installed")
      .reduce((sum, pack) => sum + packTotalBytes(pack), 0);
  }

  private installedBytes(): number {
    return this.packs
      .filter((pack) => this.states.get(pack.id) === "installed")
      .reduce((sum, pack) => sum + packTotalBytes(pack), 0);
  }

  private fitsEverything(): boolean {
    const total = this.packs.reduce((sum, pack) => sum + packTotalBytes(pack), 0);
    return total + HEADROOM_BYTES <= this.budget.available + this.installedBytes();
  }

  private overBudget(): boolean {
    return this.outstandingBytes() + HEADROOM_BYTES > this.budget.available;
  }

  private hasUsableCharts(): boolean {
    const required = this.packs.filter((pack) => pack.required);
    const requiredReady = required.every((pack) => this.states.get(pack.id) === "installed");
    const anyChart = this.packs.some(
      (pack) => !pack.required && this.states.get(pack.id) === "installed",
    );
    return requiredReady && anyChart;
  }

  private render(): void {
    const inner = document.createElement("div");
    inner.className = "setup-inner";

    const header = document.createElement("header");
    const title = document.createElement("h1");
    title.textContent = this.options.canCancel ? "Offline maps" : "Set up your maps";
    const lede = document.createElement("p");
    lede.className = "lede";
    lede.textContent = this.fitsEverything()
      ? "Every map fits on this device. Keep them all, or pick only the waters you sail."
      : "There is not room for every map on this device, so choose the waters you sail. You can add or remove areas later.";
    header.append(title, lede);
    inner.append(header);

    inner.append(this.renderStorage());

    if (this.error) {
      const notice = document.createElement("p");
      notice.className = "notice";
      notice.dataset.tone = "error";
      notice.textContent = this.error;
      inner.append(notice);
    }

    if (!this.budget.persisted && !this.busy) {
      const notice = document.createElement("p");
      notice.className = "notice";
      notice.dataset.tone = "warn";
      notice.textContent =
        "Add this app to your home screen before downloading. Maps kept in a browser tab can be cleared by the system when space runs low.";
      inner.append(notice);
    }

    inner.append(this.renderPackList());
    inner.append(this.renderActions());

    this.root.replaceChildren(inner);
  }

  private renderStorage(): HTMLElement {
    const box = document.createElement("div");
    box.className = "storage";

    const outstanding = this.outstandingBytes();
    const rows: Array<[string, string]> = [
      ["Map server", chartServer() ? new URL(chartServer()!).host : "Shared maps only"],
      ["Selected to download", formatBytes(outstanding)],
      ["Free on this device", formatBytes(this.budget.available)],
      ["Already stored", formatBytes(this.installedBytes())],
    ];
    for (const [label, value] of rows) {
      const row = document.createElement("div");
      row.className = "storage-row";
      const left = document.createElement("span");
      left.textContent = label;
      const right = document.createElement("span");
      right.textContent = value;
      row.append(left, right);
      box.append(row);
    }

    const meter = document.createElement("div");
    meter.className = "meter";
    meter.dataset.over = String(this.overBudget());
    const fill = document.createElement("i");
    const ratio = this.budget.available
      ? Math.min(1, outstanding / this.budget.available)
      : 0;
    fill.style.width = `${(ratio * 100).toFixed(1)}%`;
    meter.append(fill);
    box.append(meter);

    if (this.overBudget()) {
      const warn = document.createElement("div");
      warn.className = "storage-row";
      warn.style.color = "var(--port)";
      warn.textContent = "That is more than will fit. Deselect an area to continue.";
      box.append(warn);
    }
    return box;
  }

  private renderPackList(): HTMLElement {
    const list = document.createElement("ul");
    list.className = "packs";

    for (const pack of this.packs) {
      const state = this.states.get(pack.id) ?? "missing";
      const item = document.createElement("li");
      item.className = "pack";
      item.dataset.installed = String(state === "installed");

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = this.selection.has(pack.id);
      checkbox.disabled = this.busy || !!pack.required;
      checkbox.id = `pack-${pack.id}`;
      checkbox.addEventListener("change", () => {
        if (checkbox.checked) this.selection.add(pack.id);
        else this.selection.delete(pack.id);
        this.render();
      });

      const text = document.createElement("label");
      text.htmlFor = checkbox.id;
      const label = document.createElement("div");
      label.className = "pack-label";
      label.textContent = pack.label;
      const note = document.createElement("div");
      note.className = "pack-note";
      note.textContent = pack.required
        ? `${pack.description} · always kept`
        : pack.description ?? "";
      text.append(label, note);

      const size = document.createElement("div");
      size.className = "pack-size";
      const active = this.progress.get(pack.id);
      if (active !== undefined) {
        size.textContent = `${Math.round((active / packTotalBytes(pack)) * 100)}%`;
      } else if (state === "installed") {
        size.textContent = `✓ ${formatBytes(packTotalBytes(pack))}`;
      } else if (state === "partial") {
        size.textContent = `resume · ${formatBytes(packTotalBytes(pack))}`;
      } else {
        size.textContent = formatBytes(packTotalBytes(pack));
      }

      item.append(checkbox, text, size);

      if (active !== undefined) {
        const bar = document.createElement("div");
        bar.className = "pack-progress";
        const fill = document.createElement("i");
        fill.style.width = `${((active / packTotalBytes(pack)) * 100).toFixed(1)}%`;
        bar.append(fill);
        item.append(bar);
      }

      if (state === "installed" && !pack.required && !this.busy) {
        const remove = document.createElement("button");
        remove.className = "link";
        remove.textContent = "Remove";
        remove.addEventListener("click", async () => {
          await removePack(pack);
          this.selection.delete(pack.id);
          await this.refresh();
        });
        const holder = document.createElement("div");
        holder.style.gridColumn = "2 / -1";
        holder.append(remove);
        item.append(holder);
      }

      list.append(item);
    }
    return list;
  }

  private renderActions(): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "actions";

    if (this.busy) {
      const cancel = document.createElement("button");
      cancel.className = "button secondary";
      cancel.textContent = "Stop downloading";
      cancel.addEventListener("click", () => this.abort?.abort());
      actions.append(cancel);
      const hint = document.createElement("p");
      hint.className = "notice";
      hint.textContent =
        "Keep this screen open. Downloads pause when the app goes to the background and resume where they stopped.";
      actions.append(hint);
      return actions;
    }

    const outstanding = this.outstandingBytes();
    const download = document.createElement("button");
    download.className = "button";
    download.textContent = outstanding
      ? `Download ${formatBytes(outstanding)}`
      : "Maps are up to date";
    download.disabled = !outstanding || this.overBudget();
    download.addEventListener("click", () => void this.runDownloads());
    actions.append(download);

    const row = document.createElement("div");
    row.className = "row";

    if (this.fitsEverything()) {
      const all = document.createElement("button");
      all.className = "link";
      const everythingSelected = this.packs.every((pack) => this.selection.has(pack.id));
      all.textContent = everythingSelected ? "Select none" : "Select every area";
      all.addEventListener("click", () => {
        if (everythingSelected) {
          this.selection = new Set(
            this.packs.filter((pack) => pack.required).map((pack) => pack.id),
          );
        } else {
          this.selection = new Set(this.packs.map((pack) => pack.id));
        }
        this.render();
      });
      row.append(all);
    }

    if (this.options.canCancel || this.hasUsableCharts()) {
      const open = document.createElement("button");
      open.className = "link";
      open.textContent = "Open the map";
      open.addEventListener("click", () => this.options.onDone());
      row.append(open);
    }

    if (row.childElementCount) actions.append(row);
    return actions;
  }

  private async runDownloads(): Promise<void> {
    this.busy = true;
    this.error = null;
    this.abort = new AbortController();
    this.render();

    try {
      for (const pack of this.selectedPacks()) {
        if (this.states.get(pack.id) === "installed") continue;
        this.progress.set(pack.id, 0);
        // Chunks arrive every few kilobytes; rebuilding the screen for each one
        // would spend the whole download in layout instead of writing bytes.
        let lastPaint = 0;
        await installPack(
          pack,
          ({ received }) => {
            this.progress.set(pack.id, received);
            const now = performance.now();
            if (now - lastPaint > 150) {
              lastPaint = now;
              this.render();
            }
          },
          this.abort.signal,
        );
        this.progress.delete(pack.id);
        this.states.set(pack.id, "installed");
        this.render();
      }
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      this.error = aborted
        ? "Download stopped. Your progress is saved — start again to pick up where it left off."
        : error instanceof Error
          ? error.message
          : "Download failed.";
    } finally {
      this.busy = false;
      this.abort = null;
      this.progress.clear();
      await this.refresh();
      if (!this.error && this.hasUsableCharts()) this.options.onDone();
    }
  }
}
